import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const css = `
  .nav-burger {
    display: none;
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 102;
    width: 44px;
    height: 44px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    background: none;
    border: none;
    cursor: pointer;
  }
  .nav-burger span {
    display: block;
    width: 24px;
    height: 1px;
    background: #c9a96e;
    transition: transform 0.3s ease, opacity 0.3s ease;
  }

  @media (max-width: 768px) {
    /* Swap the horizontal menu for a full-screen overlay. */
    .nav-logo {
      left: 20px !important;
      font-size: 1.15rem !important;
      letter-spacing: 0.08em !important;
    }

    .nav-burger { display: flex; }

    .nav-links {
      position: fixed !important;
      inset: 0 !important;
      transform: none !important;
      z-index: 101;
      background: rgba(10, 10, 10, 0.985);
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      animation: navFade 0.25s ease;
    }
    .nav-links ul {
      flex-direction: column !important;
      align-items: center !important;
      gap: 34px !important;
      padding: 0 24px !important;
    }
    .nav-links li span {
      font-size: 1.3rem !important;
      letter-spacing: 0.22em !important;
      color: #f5f2ee !important;
    }
  }

  @keyframes navFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Any navigation closes the overlay menu.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Stop the page scrolling behind the open menu.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleNav = (item) => {
    if (item.scroll) {
      // If not on home page, go home first then scroll
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(item.scroll)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        document.getElementById(item.scroll)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(item.path);
    }
  };

  const pages = [
    { label: 'About', scroll: 'about' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Demo Reels', path: '/demo-reels' },
    { label: 'Cinematics', path: '/cinematics' },
    { label: 'Contact / Résumé', scroll: 'contact' },
  ];

  const navStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: scrolled ? '64px' : '80px',
    backgroundColor: scrolled ? 'rgba(10, 10, 10, 0.92)' : 'transparent',
    backdropFilter: scrolled ? 'blur(12px)' : 'none',
    borderBottom: scrolled ? '1px solid rgba(201, 169, 110, 0.15)' : 'none',
    zIndex: 100,
    transition: 'all 0.4s ease',
  };

  const logoStyle = {
    position: 'absolute',
    left: '60px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.5rem',
    fontWeight: 300,
    letterSpacing: '0.12em',
    color: '#f5f2ee',
    cursor: 'pointer',
    margin: 0,
    padding: 0,
  };

  const linksWrapperStyle = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    margin: 0,
    padding: 0,
  };

  const ulStyle = {
    display: 'flex',
    gap: '40px',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  };

  const linkStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6560',
    transition: 'color 0.2s ease',
    cursor: 'pointer',
  };

  return (
    <>
      <style>{css}</style>
      <nav style={navStyle}>
        <div
          className="nav-logo"
          style={logoStyle}
          onClick={() => {
            setMenuOpen(false);
            navigate('/');
          }}
        >
          Home
        </div>

        <div className="nav-links" style={linksWrapperStyle}>
          <ul style={ulStyle}>
            {pages.map((item) => (
              <li key={item.label} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                <span
                  style={linkStyle}
                  onClick={() => handleNav(item)}
                  onMouseEnter={(e) => (e.target.style.color = '#c9a96e')}
                  onMouseLeave={(e) => (e.target.style.color = '#6b6560')}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          className="nav-burger"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span style={{ transform: menuOpen ? 'translateY(6px) rotate(45deg)' : 'none' }} />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span style={{ transform: menuOpen ? 'translateY(-6px) rotate(-45deg)' : 'none' }} />
        </button>
      </nav>
    </>
  );
}